import { PartyAttorney } from './party-attorney';
import { SafeJSON } from './../../shared/safe-json';

export class Party {
    private json: any;
    private key: string;

    constructor(json: any, partyKey: string) {
        this.json = json;
        this.key = partyKey;
    }

    getPartyKey(): string {
        return this.key;
    }
    setPartyKey(key: string) {
        this.key = key;
    }
    getAliasList(): any[] {
        const present = SafeJSON.get('aliasList', this.json);
        if (present === '') {
            return [];
        }
        const list = Array.from(this.json.aliasList.values());
        return list;
    }
    getPartyAddressList(): any[] {
        const present = SafeJSON.get('partyAddressList', this.json);
        if (present === '') {
            return [{}];
        }
        return Array.from(this.json.partyAddressList.values());
    }
    getPartyAttorneyList(): any[] {
        // sort list by last name and put primary attorney at beginning of list
        if (SafeJSON.get('partyAttorneyList', this.json) === '') {
            return [];
        }

        let partyAttorneyListUnsorted: PartyAttorney[] = <Array<PartyAttorney>>this.json.partyAttorneyList.values();
        let partyAttorneyListSorted: PartyAttorney[] = [];
        let savePrimaryAttorney = undefined;

        // if we got multiple primary attorneys, just pick the first one we see to move to the top.
        for (const party of partyAttorneyListUnsorted) {
            const partyAttorneyItem = new PartyAttorney(party, '');
            if (partyAttorneyItem.isAttorneyPrimary() === false || savePrimaryAttorney !== undefined) {
                partyAttorneyListSorted.push(partyAttorneyItem);
            } else {
                savePrimaryAttorney = partyAttorneyItem;
            }
        }

        // Sort this list by last name (note: this sorting requires a user defined object)
        partyAttorneyListSorted.sort( function(name1, name2) {
            if (name1.getAttorneyName() < name2.getAttorneyName()) {
                return -1;
            } else if (name1.getAttorneyName() > name2.getAttorneyName()) {
                return 1;
            } else {
                return 0;
            }
        });

        if (savePrimaryAttorney !== undefined) {
            const finalPartyAttorneyListSorted: PartyAttorney[] = [];
            finalPartyAttorneyListSorted.push(savePrimaryAttorney);
            return finalPartyAttorneyListSorted.concat(partyAttorneyListSorted);
        }
        return partyAttorneyListSorted; // getting here means no primary attorney was indicated
    }
    getPartyAttorneyListType(): PartyAttorney[] {
        return <Array<PartyAttorney>>this.json.partyAttorneyList.values();
    }
    /////////////////////////////////////////////////////////////////
    getShowPrivacyMessage(address: any): boolean {
        const privacyCode: string = SafeJSON.getWithDefault('privacyIndicatorCode.code', this.json);
        const isACP = this.isAddressACP(address);

        if (privacyCode === 'ADDR' && !isACP) {
            return true;  // hide address
        }
        return false;  // not private or its an ACP address (show ACP address)
    }
    /*
        BR315.1	A party address shall be recognized as an ACP address if the Party Address is
            “ACP”
            “9099 Mail Service Center”
            “Raleigh” “NC” “27699-9099”.
    */
    isAddressACP(address: any): boolean {

        const lineOne = this.getPartyAddressesLine1(address);
        const lineTwo = this.getPartyAddressesLine2(address);
        const cityName = this.partyAddressesCityName(address);
        const stateAbbreviation = this.getPartyAddressesStateAbbreviation(address);
        const zipcode = this.getPartyAddressesZipCode(address);

        if (lineOne.includes('ACP') && lineTwo === '9099 Mail Service Center' &&
            cityName === 'Raleigh' && stateAbbreviation === 'NC' && zipcode === '27699-9099') {
                return true;
        }
        return false;
    }
    getPartyAddressIsBlank(address: any): boolean {
        const lineOne = this.getPartyAddressesLine1(address);
        const lineTwo = this.getPartyAddressesLine2(address);
        const cityName = this.partyAddressesCityName(address);
        const stateAbbreviation = this.getPartyAddressesStateAbbreviation(address);
        const zipcode = this.getPartyAddressesZipCode(address);
        if (lineOne === '-' && lineTwo === '' && cityName === '' && stateAbbreviation === '' && zipcode === '') {
            return true;
        }
        return false;
    }
    getAliasName(alias: any): string {
        return SafeJSON.getWithDefault('name', alias);
    }
    getAttorneyName(attorney: any): string {
        return SafeJSON.getWithDefault('attorney.name', attorney.json);
    }
    getAttorneyFirm(attorney: any): string {
        return SafeJSON.getWithDefault('attorney.address.firmName', attorney);
    }
    getAttorneyCity(attorney: any): string {
        return SafeJSON.getWithDefault('attorney.address.cityName', attorney);
    }
    getAttorneyPrimaryIndicator(attorney: any): string {
        const indicator: string = SafeJSON.get('primaryIndicator', attorney.json);
        if (indicator === 'Y') {
            return '(Primary)';
        }
        return '';
    }
    getAttorneyBarNumber(attorney: any): string {
        const noBarNumber = 'No North Carolina State Bar number was recorded';
        let barNumber: string = SafeJSON.getWithDefault('attorney.barNumber', attorney.json);
        if (barNumber === '-' && this.getAttorneyName(attorney) !== '-') {
            return noBarNumber;
        }
        const num = this.convertToNumber(barNumber);
        barNumber = num.toString();
        return barNumber;
    }
    getPartyAddressesLine1(partyAddress: any): string {
        return SafeJSON.getWithDefault('address.line1', partyAddress);
    }
    getPartyAddressesLine2(partyAddress: any): string {
        return SafeJSON.get('address.line2', partyAddress);
    }
    partyAddressesCityStateZipCodeName(partyAddress: any): string {
        const cityName: string = this.partyAddressesCityName(partyAddress);
        if (cityName === '') {
            return '';
        }
        return cityName + this.getPartyAddressesStateAbbreviation(partyAddress) +
                           ' ' + this.getPartyAddressesZipCode(partyAddress);
    }
    partyAddressesCityName(partyAddress: any): string {
        return SafeJSON.get('address.cityName', partyAddress);
    }
    getPartyAddressesStateAbbreviation(partyAddress: any): string {
        const state = SafeJSON.get('address.stateAbbreviation.code', partyAddress);
        if (state !== '') {
            return ', ' + state;
        }
        return '';
    }
    getPartyAddressesZipCode(partyAddress: any): string {
        const zipCode5 = SafeJSON.get('address.zipCode', partyAddress);
        const zipCode4 = SafeJSON.get('address.zip4', partyAddress);
        if (zipCode4 !== '') {
            return zipCode5 + '-' + zipCode4;
        }
        return zipCode5;
    }
    getPartyCounty(partyAddress: any): string {
        const county: string = SafeJSON.get('address.countyResidence', partyAddress);  // TODO  Ramana needs to add this
        if (county === '') {
            return '';
        }
        return county + ' COUNTY';
    }
    getPartyCountry(partyAddress: any): string {
        let country: string = SafeJSON.get('address.countryAbbreviation', partyAddress);
        if (country === 'US') {
            return '';
        }
        if (country === 'MX') {
            country = 'MEXICO';
        }
        if (country === 'CN') {
            country = 'CANADA';
        }
        let separator = '';
        if (this.getPartyCounty(partyAddress) !== '') {
            separator = ', ';
        }
        return separator + country;
    }
    getPartyPhoneNumber(partyAddress: any): string {
        const privacy = this.getShowPrivacyMessage(partyAddress);
        const BLANKNUMBER = '   -   -    ';
        if (privacy === true) {
            return 'Phone number is confidential.';
        }
        let phoneNumber = SafeJSON.getWithDefault('address.phoneNumber', partyAddress);
        const extension = SafeJSON.get('address.phoneNumberExtension', partyAddress);
        if (phoneNumber === BLANKNUMBER) {
            phoneNumber = '-';
        }
        if (extension === '' || phoneNumber === '-') {
            return phoneNumber;
        }
        return phoneNumber + 'x' + extension;
    }
    getPartyFaxNumber(partyAddress: any): string {
        const BLANKNUMBER = '   -   -    ';

        let faxNumber = SafeJSON.getWithDefault('address.faxNumber', partyAddress);
        if (faxNumber === BLANKNUMBER) {
            faxNumber = '-';
        }
        return faxNumber;
    }
    getName(): string {
        return SafeJSON.getWithDefault('name', this.json);
    }
    getPartyDate(): string {
        return SafeJSON.get('date', this.json);
    }
    getPartyDateLabel(juvenileCase: any): string {
        const caseType = juvenileCase.getCaseType();
        const partyTitle = this.getTitleCode();

        if (caseType === 'E' || caseType === 'EO' && partyTitle === 'F') {
            return 'Date of Qualification';
        }
        if ( partyTitle === 'B') {  // TODO  get correct code
            return 'Date of Death';
        }
        if (caseType === 'E' || caseType === 'EO' && partyTitle === 'E') {
            return 'Year';
        }
        if (partyTitle === 'O') {   // TODO  get correct code
            return 'Date of Expiration';
        }
        return 'Date';
    }
    getPartyRole(): string {
        return SafeJSON.getWithDefault('partyRole.description', this.json);
    }
    getPartyRoleCode(): string {
        return SafeJSON.getWithDefault('partyRole.code', this.json);
    }
    /*
        B = BENEFICIARY / DEVISEE
        D = DEFENDANT
        E = ESTATE
        F = FIDUCIARY
        I = INTERESTED PARTY
        P = PLAINTIFF / PETITIONER
        R = RESPONDENT
        S = SURETY
    */
    getPartyRoleSortNumber(caseType: string): Number {
        const partyCode = this.getPartyRoleCode();
        if (caseType === 'CVM' || caseType === 'CVD' || caseType === 'CVS') {
            if (partyCode === 'P') {
                return 1;
            }
            if (partyCode === 'D') {
                return 2;
            }
            if (partyCode === 'I') {
                return 3;
            }
        }

        if (caseType === 'CR' || caseType === 'CRS' || caseType === 'J') {
            if (partyCode === 'P') {
                return 1;
            }
            if (partyCode === 'D') {
                return 2;
            }
            if (partyCode === 'F') {
                return 3;
            }
            if (partyCode === 'S') {
                return 4;
            }
        }

        if (caseType === 'E' || caseType === 'EO' || caseType === 'J') {
            if (partyCode === 'E') {
                return 1;
            }
            if (partyCode === 'F') {
                return 2;
            }
            if (partyCode === 'B') {
                return 3;
            }
        }
        if (caseType === 'SP') {
            if (partyCode === 'P') {
                return 1;
            }
            if (partyCode === 'R') {
                return 2;
            }
            if (partyCode === 'E') {
                return 3;
            }
            if (partyCode === 'F') {
                return 4;
            }
            if (partyCode === 'S') {
                return 5;
            }
        }
        if (caseType === 'R') {
            if (partyCode === 'P') {
                return 1;
            }
            if (partyCode === 'R') {
                return 2;
            }
        }
        if (caseType === 'M') {
            if (partyCode === 'P') {
                return 1;
            }
            if (partyCode === 'D') {
                return 2;
            }
            if (partyCode === 'E') {
                return 3;
            }
            if (partyCode === 'F') {
                return 4;
            }
            if (partyCode === 'S') {
                return 5;
            }
        }
        return 0;
    }
    getTitle(): string {
        return SafeJSON.getWithDefault('partyTitle.partyTitle.description', this.json);
    }
    getTitleCode(): string {
        return SafeJSON.getWithDefault('partyTitle.partyTitle.code', this.json);
    }
    getPartyNumber(): number {
        return SafeJSON.get('partyNumber', this.json);
    }
    getStatus(): string {
        return SafeJSON.getWithDefault('status', this.json);
    }
    getHasJudgements(): string {
        return SafeJSON.get('judgmentIndexed', this.json);
    }
    getReasonFilingFeeDeferred(): string {
        return SafeJSON.getWithDefault('indigencyIndicator.description', this.json);
    }
    getDepartmentOfInsuranceLicenseNumber(juvenileCase: any): string {
        const caseType = juvenileCase.getCaseType();
        if (caseType === 'CR' || caseType === 'CRS') {
            const partyType = this.getTitleCode();
            if (partyType === 'F' || partyType === 'S') {
                return SafeJSON.getWithDefault('suretySequenceNumber', this.json);
            }
        }
        return '-';
    }
    convertToNumber(jsonField: string): number {
        if (jsonField === '') {
            return 0;
        }
        const num = Number.parseInt(jsonField);
        return num;
    }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/juvenile/juvenile-case-sections/party.ts